export const MAX_CONCURRENT_UPLOADS = 3;
export const PROGRESS_THROTTLE_MS = 250;
export const PROGRESS_THROTTLE_DELTA_PCT = 1;
export const CANCEL_REASON = 'cancelled by user';

/** How often the processing phase asks the backend where the import got to. */
export const PROCESS_POLL_INTERVAL_MS = 5_000;

/**
 * Consecutive status-poll failures tolerated before the item is failed.
 */
export const PROCESS_POLL_MAX_FAILURES = 5;

/**
 * How many times the details flush is retried while the draft keeps moving. An edit can land
 * while the previous PATCH is in flight, and once the item completes its details can no longer
 * be flushed, so the last flush re-reads until the draft settles — bounded, because a user who
 * keeps typing must not hold the row open indefinitely.
 */
export const DETAILS_FLUSH_MAX_ROUNDS = 3;

/**
 * The progress bar's budget per phase, so one bar can span the whole pipeline and only ever move
 * forward: analyze owns 0–25, the upload 25–95, and processing the last five points.
 */
export const PHASE_PROGRESS = {
  ANALYZE_END: 25,
  UPLOAD_END: 95,
  PROCESS_END: 100,
} as const;

/**
 * Where an attempt gave up. The queue keeps the backend's own `message` alongside the code when
 * there is one; the code is what CR-7 translates.
 */
export const UPLOAD_ERROR_CODE = {
  ANALYZE_FAILED: 'ANALYZE_FAILED',
  /** Rejected by the accept guard: a zero-byte file. */
  EMPTY_FILE: 'EMPTY_FILE',
  /** The registry has no File for the id — the queue outlived its handles (a reload, say). */
  FILE_MISSING: 'FILE_MISSING',
  /** Rejected by the accept guard: over `MAX_UPLOAD_FILE_BYTES`. */
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  /** The signed-url response named an import we cannot address, so processing is unpollable. */
  IMPORT_ID_MISSING: 'IMPORT_ID_MISSING',
  PROCESS_FAILED: 'PROCESS_FAILED',
  SIGNED_URL_FAILED: 'SIGNED_URL_FAILED',
  STATUS_UNAVAILABLE: 'STATUS_UNAVAILABLE',
  /** Rejected by the accept guard: a container outside the allowlist. */
  UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
} as const;

/** Listed in lifecycle order rather than alphabetically — the order is the state machine. */
export const UPLOAD_PHASE = {
  QUEUED: 'queued',
  ANALYZING: 'analyzing',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
  ERROR: 'error',
  CANCELLED: 'cancelled',
} as const;

type _UploadPhase = (typeof UPLOAD_PHASE)[keyof typeof UPLOAD_PHASE];

/** Phases where work is still outstanding — the item is cancellable and its details are editable. */
export const ACTIVE_PHASES: ReadonlySet<_UploadPhase> = new Set([
  UPLOAD_PHASE.QUEUED,
  UPLOAD_PHASE.ANALYZING,
  UPLOAD_PHASE.UPLOADING,
  UPLOAD_PHASE.PROCESSING,
]);

/** Phases where nothing more happens without user input (retry / remove). */
export const TERMINAL_PHASES: ReadonlySet<_UploadPhase> = new Set([
  UPLOAD_PHASE.COMPLETE,
  UPLOAD_PHASE.ERROR,
  UPLOAD_PHASE.CANCELLED,
]);

/** Listed in lifecycle order, then the user-initiated events. */
export const UPLOAD_EVENT = {
  START: 'START',
  PROGRESS: 'PROGRESS',
  ANALYZE_DONE: 'ANALYZE_DONE',
  UPLOAD_DONE: 'UPLOAD_DONE',
  MEDIA_ID: 'MEDIA_ID',
  PROCESS_DONE: 'PROCESS_DONE',
  FAIL: 'FAIL',
  UPDATE_DETAILS: 'UPDATE_DETAILS',
  CANCEL: 'CANCEL',
  RETRY: 'RETRY',
  REMOVE: 'REMOVE',
} as const;

/** What the queue reducer decided to do with an event. */
export const REDUCE_RESULT = {
  IGNORED: 'ignored',
  REMOVED: 'removed',
  UPDATE: 'update',
} as const;

export const CLIENT_BOUND_PHASES: ReadonlySet<_UploadPhase> = new Set([
  UPLOAD_PHASE.QUEUED,
  UPLOAD_PHASE.ANALYZING,
  UPLOAD_PHASE.UPLOADING,
]);

/** Panel widths in px, applied only from `xl` up — below that the panel is a full-viewport sheet. */
export const UPLOAD_PANEL_WIDTH = 480;
export const UPLOAD_PANEL_MINIMIZED_WIDTH = 400;

export const SUGGESTED_FILES_PER_DROP = 10;

export const UPLOAD_PANEL_STATE = {
  EMPTY: 'empty',
  ACTIVE: 'active',
  COMPLETE: 'complete',
  COMPLETE_WITH_ERRORS: 'completeWithErrors',
  ERRORS: 'errors',
} as const;

/**
 * Per-file ceiling for the client-side accept guard. Decimal GB, the same base the row sizes and
 * the library storage figures use, so a file the panel shows as over "64 GB" is the one refused.
 * The backend allows 64 GiB, so this is the stricter of the two.
 */
export const MAX_UPLOAD_FILE_BYTES = 64 * 1000 ** 3;

/**
 * Containers the accept guard lets through. Codecs, resolution and framerate are deliberately not
 * checked client-side — the backend rejects those (ProRes-in-.mov, say) during processing.
 */
export const UPLOAD_CONTAINERS = {
  /** What the single-file flow has always taken; used until the wider set is enabled. */
  MP4_ONLY: [{ extension: '.mp4', mimeType: 'video/mp4' }],
  UNIVERSAL: [
    { extension: '.mp4', mimeType: 'video/mp4' },
    { extension: '.mov', mimeType: 'video/quicktime' },
    { extension: '.webm', mimeType: 'video/webm' },
    { extension: '.mkv', mimeType: 'video/x-matroska' },
  ],
} as const;
